import type { DragEvent } from 'react';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { MdOutlineDragIndicator } from 'react-icons/md';
import { Button, cn, FormCheckBox, FormInput, FormSelect, FormTextArea, Insight } from '@/core/ui';
import type { IMarkSchema } from './schema';

const MARK_WEIGHT_OPTIONS = [
  { key: '1', label: '1' },
  { key: '2', label: '2' },
  { key: '3', label: '3' },
];

const reindexMarks = (marks: IMarkSchema[]): IMarkSchema[] => {
  let counter = 0;
  return marks.map((mark, headerIndex) => ({
    ...mark,
    index: headerIndex + 1,
    choices: mark.choices.map((choice) => ({
      ...choice,
      index: ++counter,
    })),
  }));
};

const _StationMarks = ({
  className,
  scrollContainerClassName,
  showInfo = true,
}: {
  className?: string;
  scrollContainerClassName?: string;
  showInfo?: boolean;
}) => {
  const { control, setValue, getValues } = useFormContext<{ marks: IMarkSchema[] }>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { fields, append: appendMark, remove: removeMark, move } = useFieldArray({ control, name: 'marks' });

  const setMarks = useCallback(
    (updater: (current: IMarkSchema[]) => IMarkSchema[]) => {
      const scrollTop = scrollRef.current?.scrollTop ?? 0;

      const current = getValues('marks') ?? [];
      const next = reindexMarks(updater(current));

      setValue('marks', next, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollTop;
        }
      });
    },
    [getValues, setValue],
  );

  const handleAddHeader = () => {
    const lastField = fields[fields.length - 1];
    const lastChoice = lastField?.choices[lastField?.choices.length - 1];
    const nextIndex = lastChoice?.index ? lastChoice.index + 1 : 1;
    appendMark({ header: '', choices: [{ name: '', index: nextIndex, weight: 1, excludeFromAi: false }] });
  };

  const handleDeleteHeader = (markIndex: number) => {
    removeMark(markIndex);
  };

  const handleAddChoiceToHeader = (markIndex: number) => {
    setMarks((current) =>
      current.map((mark, idx) =>
        idx === markIndex
          ? { ...mark, choices: [...mark.choices, { name: '', index: 1, weight: 1, excludeFromAi: false }] }
          : mark,
      ),
    );
  };

  const handleDeleteChoice = (markIndex: number, choiceIndex: number) => {
    setMarks((current) =>
      current.map((mark, idx) => {
        if (idx !== markIndex) return mark;
        if (mark.choices.length <= 1) return mark;
        return {
          ...mark,
          choices: mark.choices.filter((_, cIdx) => cIdx !== choiceIndex),
        };
      }),
    );
  };

  const handleChoiceDragStart = (e: DragEvent<HTMLDivElement>, markIndex: number, choiceIndex: number) => {
    e.stopPropagation();
    e.dataTransfer.setData('markIndex', String(markIndex));
    e.dataTransfer.setData('choiceIndex', String(choiceIndex));
  };

  const handleChoiceDrop = (e: DragEvent<HTMLDivElement>, markIndex: number, choiceIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    const fromMarkIndex = Number(e.dataTransfer.getData('markIndex'));
    const fromChoiceIndex = Number(e.dataTransfer.getData('choiceIndex'));

    if (
      Number.isNaN(fromMarkIndex) ||
      Number.isNaN(fromChoiceIndex) ||
      fromMarkIndex !== markIndex ||
      fromChoiceIndex === choiceIndex
    ) {
      return;
    }

    setMarks((current) =>
      current.map((mark, idx) => {
        if (idx !== markIndex) return mark;

        const choices = [...mark.choices];
        const [moved] = choices.splice(fromChoiceIndex, 1);
        choices.splice(choiceIndex, 0, moved);

        return {
          ...mark,
          choices,
        };
      }),
    );
  };

  const handleHeaderDragStart = (e: DragEvent<HTMLDivElement>, markIndex: number) => {
    e.stopPropagation();
    e.dataTransfer.setData('index', String(markIndex));
  };

  const handleHeaderDrop = (e: DragEvent<HTMLDivElement>, markIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const from = Number(e.dataTransfer.getData('index'));
    if (Number.isNaN(from) || from === markIndex) return;

    move(from, markIndex);

    const current = (getValues('marks') as IMarkSchema[] | undefined) ?? [];
    const next = reindexMarks(current);
    setValue('marks', next, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  };

  const counts = useMemo(() => {
    return {
      headers: fields.length,
      choices: fields.reduce((acc, mark) => acc + mark.choices.length, 0),
      excludedFromAi: fields.reduce(
        (acc, mark) => acc + mark.choices.filter((choice) => choice.excludeFromAi).length,
        0,
      ),
    };
  }, [fields]);

  return (
    <div className={cn('', className)}>
      <div className='flex mb-4 flex-row items-center justify-between gap-2'>
        <div className='flex flex-row items-center gap-2'>
          <span className='text-default-500 text-sm'>
            MarkScheme:&nbsp;Headers ({counts.headers}), Marks ({counts.choices}), Excluded from AI (
            {counts.excludedFromAi})
          </span>
        </div>
        <div className='flex flex-row items-center gap-2'>
          <Button size='sm' variant='bordered' className='gap-2' onPress={handleAddHeader}>
            <BsPlus className='size-4' />
            Add
          </Button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className={cn(
          'flex-1 overflow-y-auto max-h-[calc(100dvh-290px)] space-y-4',
          'scrollbar-thin scrollbar-thumb-default-200 scrollbar-track-default-100',
          scrollContainerClassName,
        )}
      >
        {showInfo && (
          <Insight
            variant='faded'
            title='Station marks'
            color='secondary'
            description='Create headers and add one or more choices for each. Each choice gets an auto-generated index, unique across all headers, starting from 1.'
          />
        )}

        {fields.length === 0 && (
          <div className='text-default-400 text-sm border border-dashed rounded-lg p-4 text-center'>
            No marks yet. Click &ldquo;Add header&rdquo; to get started.
          </div>
        )}

        <div className='space-y-4'>
          {fields.map((mark, markIndex) => {
            return (
              <div
                key={mark.id ?? markIndex}
                className='border border-dashed border-default-400 rounded p-3 space-y-3 cursor-move'
                draggable
                onDragStart={(e) => handleHeaderDragStart(e, markIndex)}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => handleHeaderDrop(e, markIndex)}
              >
                <div className='sticky top-0 z-10 bg-background border-b border-default-200 pb-4 pt-1 -mt-1 mb-1'>
                  <div className='flex flex-col space-y-2'>
                    <div className='flex items-center justify-between gap-2 '>
                      <span className='cursor-grab active:cursor-grabbing bg-warning-50 p-1.5 border border-warning-200 rounded text-sm text-warning-500 size-8'>
                        <MdOutlineDragIndicator className='size-4' />
                      </span>
                      <span className='bg-secondary-50 p-1.5 border border-secondary-100 rounded text-sm text-secondary-500 size-8'>
                        #{markIndex + 1}
                      </span>
                      <FormInput
                        id={`marks.${markIndex}.header`}
                        control={control}
                        name={`marks.${markIndex}.header`}
                        variant='faded'
                        className='w-full'
                        placeholder='Enter header'
                      />
                      <div className='flex justify-end gap-2'>
                        <Button
                          size='sm'
                          color='danger'
                          radius='sm'
                          variant='faded'
                          title='Delete header, it will delete all choices attached to it'
                          isIconOnly
                          onPress={() => handleDeleteHeader(markIndex)}
                        >
                          <BsTrash className='mr-1 size-3' />
                        </Button>
                        <Button
                          size='sm'
                          variant='faded'
                          title='Add choice'
                          radius='sm'
                          isIconOnly
                          className='gap-2'
                          onPress={() => handleAddChoiceToHeader(markIndex)}
                        >
                          <BsPlus className='size-4' />
                        </Button>
                      </div>
                    </div>
                    <span className='text-xs text-default-500'>
                      Marks with the checkbox checked will not be included in AI chat/voice markscheme.
                    </span>
                  </div>
                </div>

                <div className='space-y-2'>
                  {mark.choices.map((choice, choiceIndex) => (
                    <div
                      key={`${choice.index}-${
                        // biome-ignore lint/suspicious/noArrayIndexKey: <keep the key unique>
                        choiceIndex
                      }`}
                      className='flex items-start justify-between gap-2 cursor-move'
                      draggable
                      onDragStart={(e) => handleChoiceDragStart(e, markIndex, choiceIndex)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={(e) => handleChoiceDrop(e, markIndex, choiceIndex)}
                    >
                      <div className='flex flex-col gap-1 pt-1'>
                        <span className='cursor-grab active:cursor-grabbing bg-warning-50 p-1 border border-warning-100 rounded text-sm text-warning-500 size-6'>
                          <MdOutlineDragIndicator className='size-3.5' />
                        </span>
                        <div className='flex items-center justify-center bg-default-100 p-0.5 border border-default-200 rounded text-xs text-default-500'>
                          #{choice.index}-{choiceIndex}
                        </div>
                      </div>
                      <div className='flex-1 flex items-start gap-2'>
                        <FormTextArea
                          isRequired
                          maxRows={3}
                          id={`marks.${markIndex}.choices.${choiceIndex}.name`}
                          control={control}
                          name={`marks.${markIndex}.choices.${choiceIndex}.name`}
                          variant='faded'
                          className='text-sm'
                          placeholder='Enter choice name'
                        />
                      </div>
                      <div className='w-16 flex flex-col space-y-2'>
                        <div className='flex flex-row items-center overflow-hidden'>
                          <FormCheckBox
                            name={`marks.${markIndex}.choices.${choiceIndex}.excludeFromAi`}
                            control={control}
                            // defaultValue={false}
                            // defaultSelected={choice.excludeFromAi || false}
                            size='lg'
                            title='Exclude from AI'
                            className='whitespace-nowrap rounded-xl'
                          ></FormCheckBox>
                          <Button
                            size='sm'
                            color='danger'
                            isIconOnly
                            variant='faded'
                            isDisabled={mark.choices.length <= 1}
                            onPress={() => handleDeleteChoice(markIndex, choiceIndex)}
                            aria-label='Delete choice'
                          >
                            <BsTrash className='size-3' />
                          </Button>
                        </div>
                        <FormSelect
                          isRequired
                          id={`marks.${markIndex}.choices.${choiceIndex}.weight`}
                          name={`marks.${markIndex}.choices.${choiceIndex}.weight`}
                          control={control}
                          options={MARK_WEIGHT_OPTIONS}
                          transform={(value) => Number.parseInt(value, 10)}
                          variant='faded'
                          // label='Weight'
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const StationMarks = memo(_StationMarks);
